/**
 * Mapa centralizado de entidades do sistema.
 * Single source of truth para nomes, ícones, slugs e descrições.
 */

export interface EntityInfo {
	key: string;
	name: string;
	namePlural: string;
	slug: string | null;
	icon: string;
	description: string;
}

export const ENTITY_MAP: Record<string, EntityInfo> = {
	// ========== Modelos do banco (Prisma) ==========
	account: {
		key: "account",
		name: "Conta",
		namePlural: "Contas",
		slug: null,
		icon: "mdi-account-circle",
		description: "Perfil global do usuário (email, displayName, etc.)",
	},
	temple: {
		key: "temple",
		name: "Templo",
		namePlural: "Templos",
		slug: null,
		icon: "mdi-church",
		description: "Organização/terreiro, unidade de multi-tenancy",
	},
	plan: {
		key: "plan",
		name: "Plano",
		namePlural: "Planos",
		slug: "/temple/plan",
		icon: "mdi-credit-card",
		description: "Plano de assinatura do templo",
	},
	user: {
		key: "user",
		name: "Usuário",
		namePlural: "Usuários",
		slug: "/temple/users",
		icon: "mdi-account-cog",
		description: "Vínculo Account↔Temple com papel (OWNER/ADMIN/MEMBER)",
	},
	userInvite: {
		key: "userInvite",
		name: "Convite",
		namePlural: "Convites",
		slug: "/temple/invites",
		icon: "mdi-account-plus",
		description: "Convite para ingressar no templo",
	},
	person: {
		key: "person",
		name: "Pessoa",
		namePlural: "Pessoas",
		slug: null,
		icon: "mdi-account",
		description: "Cadastro de pessoa (consulente ou médium)",
	},
	spiritualLine: {
		key: "spiritualLine",
		name: "Linha Espiritual",
		namePlural: "Linhas Espirituais",
		slug: "/spiritual-lines",
		icon: "mdi-star-four-points",
		description: "Linha espiritual",
	},
	orixa: {
		key: "orixa",
		name: "Orixá",
		namePlural: "Orixás",
		slug: "/orixas",
		icon: "mdi-crown-circle",
		description: "Orixá do templo",
	},
	guide: {
		key: "guide",
		name: "Guia Espiritual",
		namePlural: "Guias Espirituais",
		slug: "/guides",
		icon: "mdi-star-circle",
		description: "Entidade/guia espiritual",
	},
	ritualType: {
		key: "ritualType",
		name: "Tipo de Ritual",
		namePlural: "Tipos de Rituais",
		slug: "/ritual-types",
		icon: "mdi-shape",
		description: "Classificação de rituais (gira, sessão, etc.)",
	},
	ritual: {
		key: "ritual",
		name: "Ritual",
		namePlural: "Rituais",
		slug: "/rituals",
		icon: "mdi-calendar-star",
		description: "Evento ritualístico agendado",
	},
	journeyStage: {
		key: "journeyStage",
		name: "Jornada Mediúnica",
		namePlural: "Jornadas Mediúnicas",
		slug: "/journey-stages",
		icon: "mdi-transit-connection-variant",
		description: "Estágio de desenvolvimento mediúnico",
	},
	function: {
		key: "function",
		name: "Função",
		namePlural: "Funções",
		slug: "/functions",
		icon: "mdi-account-hard-hat",
		description: "Cargo/função ritual (cambono, ogã, etc.)",
	},
	personFunction: {
		key: "personFunction",
		name: "Pessoa-Função",
		namePlural: "Pessoas-Funções",
		slug: null,
		icon: "mdi-account-hard-hat",
		description: "Relação N:N entre Person e Function",
	},
	guidePerson: {
		key: "guidePerson",
		name: "Guia-Pessoa",
		namePlural: "Guias-Pessoas",
		slug: null,
		icon: "mdi-star-circle",
		description: "Relação N:N entre Guide e Person",
	},
	ticket: {
		key: "ticket",
		name: "Ficha",
		namePlural: "Fichas",
		slug: "/tickets",
		icon: "mdi-ticket",
		description: "Ficha de atendimento espiritual",
	},
	attendanceType: {
		key: "attendanceType",
		name: "Tipo de Atendimento",
		namePlural: "Tipos de Atendimento",
		slug: "/attendance-types",
		icon: "mdi-card-account-details",
		description: "Template reutilizável de ficha de atendimento",
	},
	attendanceCard: {
		key: "attendanceCard",
		name: "Ficha de Atendimento",
		namePlural: "Fichas de Atendimento",
		slug: null,
		icon: "mdi-card-account-details",
		description: "Vínculo entre tipo de atendimento e tipo de ritual",
	},

	// ========== Conceitos de UI (sem modelo próprio) ==========
	dashboard: {
		key: "dashboard",
		name: "Dashboard",
		namePlural: "Dashboards",
		slug: "/dashboard",
		icon: "mdi-view-dashboard",
		description: "Painel principal",
	},
	consultant: {
		key: "consultant",
		name: "Consulente",
		namePlural: "Consulentes",
		slug: "/consultants",
		icon: "mdi-account-heart",
		description: "Pessoa com type=CONSULTANT (filtro de Person)",
	},
	medium: {
		key: "medium",
		name: "Médium",
		namePlural: "Médiuns",
		slug: "/mediums",
		icon: "mdi-account-star",
		description: "Pessoa com type=MEDIUM (filtro de Person)",
	},
	ritualAttendance: {
		key: "ritualAttendance",
		name: "Atendimento Ritual",
		namePlural: "Atendimentos Rituais",
		slug: "/ritual/:id/atendimento",
		icon: "mdi-clipboard-check",
		description: "Gestão de presença e atendimento num ritual",
	},
	ticketCall: {
		key: "ticketCall",
		name: "Chamada de Fichas",
		namePlural: "Chamadas de Fichas",
		slug: "/ticket-call",
		icon: "mdi-bullhorn",
		description: "Painel de chamada de fichas em tempo real",
	},
	support: {
		key: "support",
		name: "Suporte",
		namePlural: "Chamados",
		slug: "/support",
		icon: "mdi-lifebuoy",
		description: "Chamados de suporte técnico",
	},
	templeSettings: {
		key: "templeSettings",
		name: "Configurações",
		namePlural: "Configurações",
		slug: "/temple/settings",
		icon: "mdi-cog",
		description: "Configurações do templo",
	},
	report: {
		key: "report",
		name: "Relatório",
		namePlural: "Relatórios",
		slug: "/reports",
		icon: "mdi-file-chart",
		description: "Relatórios de rituais fechados",
	},
	displayMessage: {
		key: "displayMessage",
		name: "Display / TV",
		namePlural: "Mensagens de Display",
		slug: "/temple/display-messages",
		icon: "mdi-television-play",
		description: "Mensagens e vídeos exibidos na TV do templo",
	},

	// ========== Member Groups ==========
	memberGroup: {
		key: "memberGroup",
		name: "Grupo",
		namePlural: "Grupos",
		slug: "/member-groups",
		icon: "mdi-account-group",
		description: "Grupos de membros do templo",
	},

	// ========== Financial ==========
	financial: {
		key: "financial",
		name: "Financeiro",
		namePlural: "Financeiro",
		slug: "/financial",
		icon: "mdi-cash-multiple",
		description: "Gestão financeira do templo",
	},
	fundCategory: {
		key: "fundCategory",
		name: "Categoria",
		namePlural: "Categorias",
		slug: "/financial/settings",
		icon: "mdi-tag-text",
		description: "Categorias de fundos",
	},
	fund: {
		key: "fund",
		name: "Fundo",
		namePlural: "Fundos",
		slug: "/financial/funds",
		icon: "mdi-wallet",
		description: "Gestão de fundos financeiros",
	},
	finMovement: {
		key: "finMovement",
		name: "Movimentação",
		namePlural: "Movimentações",
		slug: "/financial/movements",
		icon: "mdi-swap-horizontal",
		description: "Movimentações financeiras",
	},
	recurringExpense: {
		key: "recurringExpense",
		name: "Despesa Recorrente",
		namePlural: "Despesas Recorrentes",
		slug: "/financial/recurring-expenses",
		icon: "mdi-calendar-sync",
		description: "Despesas que se repetem automaticamente todo mês",
	},
	wisdomQuote: {
		key: "wisdomQuote",
		name: "Frase de Sabedoria",
		namePlural: "Frases de Sabedoria",
		slug: "/temple/wisdom-quotes",
		icon: "mdi-book-open-page-variant",
		description: "Frases de sabedoria e ensinamentos espirituais do templo",
	},
	import: {
		key: "import",
		name: "Importação",
		namePlural: "Importações",
		slug: "/temple/import",
		icon: "mdi-file-upload",
		description: "Importação de dados via planilha XLSX",
	},
} as const;

export function getEntity(key: string): EntityInfo | undefined {
	return ENTITY_MAP[key];
}

export function getEntityIcon(key: string): string {
	return ENTITY_MAP[key]?.icon ?? "mdi-help-circle";
}
